import { FC } from 'react';
import { Input, Button } from '@/components/ui';
import { X } from 'lucide-react';

interface AdditionalFieldsSectionProps {
  cta: string;
  bullets: string[];
  date: string;
  time: string;
  speakerRole: string;
  phone: string;
  website: string;
  onCtaChange: (value: string) => void;
  onBulletsChange: (bullets: string[]) => void;
  onDateChange: (value: string) => void;
  onTimeChange: (value: string) => void;
  onSpeakerRoleChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onWebsiteChange: (value: string) => void;
  shouldShowBullets: boolean;
  shouldShowDate: boolean;
  shouldShowTime: boolean;
  shouldShowSpeakerRole: boolean;
  shouldShowPhone: boolean;
  shouldShowWebsite: boolean;
  disabled?: boolean;
}

export const AdditionalFieldsSection: FC<AdditionalFieldsSectionProps> = ({
  cta,
  bullets,
  date,
  time,
  speakerRole,
  phone,
  website,
  onCtaChange,
  onBulletsChange,
  onDateChange,
  onTimeChange,
  onSpeakerRoleChange,
  onPhoneChange,
  onWebsiteChange,
  shouldShowBullets,
  shouldShowDate,
  shouldShowTime,
  shouldShowSpeakerRole,
  shouldShowPhone,
  shouldShowWebsite,
  disabled = false,
}) => {
  const addBullet = () => onBulletsChange([...bullets, '']);
  
  const updateBullet = (index: number, value: string) =>
    onBulletsChange(bullets.map((b, i) => (i === index ? value : b)));
  
  const removeBullet = (index: number) =>
    onBulletsChange(bullets.filter((_, i) => i !== index));

  return (
    <>
      <Input
        label="Contact Us (CTA)"
        value={cta}
        onChange={(e) => onCtaChange(e.target.value)}
        placeholder="Enter call-to-action text"
        fullWidth
        disabled={disabled}
      />

      {shouldShowBullets && (
        <div className="space-y-2">
          <label className="block text-xs font-medium text-slate-600 mb-1.5">
            Bullets
          </label>
          <div className="space-y-2">
            {bullets.map((bullet, idx) => (
              <div key={idx} className="flex gap-2">
                <Input
                  value={bullet}
                  onChange={(e) => updateBullet(idx, e.target.value)}
                  placeholder={`Bullet ${idx + 1}`}
                  fullWidth
                  disabled={disabled}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-red-600 hover:text-red-700"
                  onClick={() => removeBullet(idx)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
            {!disabled && (
              <Button
                onClick={addBullet}
                variant="secondary"
                size="sm"
                className="text-xs"
              >
                Add bullet
              </Button>
            )}
          </div>
        </div>
      )}

      {shouldShowDate && (
        <Input
          label="Date"
          type="date"
          value={date}
          onChange={(e) => onDateChange(e.target.value)}
          placeholder="YYYY-MM-DD"
          fullWidth
          disabled={disabled}
        />
      )}

      {shouldShowTime && (
        <Input
          label="Time"
          type="time"
          value={time}
          onChange={(e) => onTimeChange(e.target.value)}
          placeholder="HH:MM"
          fullWidth
          disabled={disabled}
        />
      )}

      {shouldShowSpeakerRole && (
        <Input
          label="Speaker Role"
          value={speakerRole}
          onChange={(e) => onSpeakerRoleChange(e.target.value)}
          placeholder="e.g., Founder and CEO"
          fullWidth
          disabled={disabled}
        />
      )}

      {shouldShowPhone && (
        <Input
          label="Phone"
          value={phone}
          onChange={(e) => onPhoneChange(e.target.value)}
          placeholder="+ 123 456 789"
          fullWidth
          disabled={disabled}
        />
      )}

      {shouldShowWebsite && (
        <Input
          label="Website"
          value={website}
          onChange={(e) => onWebsiteChange(e.target.value)}
          placeholder="https://www.example.com"
          fullWidth
          disabled={disabled}
        />
      )}
    </>
  );
};

